import { isUndefined } from 'lodash';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { type DiscussionLocation, useTrackDeletedComment } from '@air/analytics';
import { type Annotation, type Clip, type CommentResponse } from '@air/api/types';

import { getAnnotationAnalyticsType, isAnnotationCorrect } from '~/components/Annotations/shared/annotationUtils';
import { useUpdateStateAfterAddingOrRemovingDiscussion } from '~/components/DiscussionsV2/utils';
import {
  centralizedClipAssetIdSelector,
  centralizedClipSelector,
  centralizedClipTypeSelector,
} from '~/store/centralizedClip/selectors';
import { useDeleteComment } from '~/swr-hooks/discussions/useDeleteComment';
import { getBoardIdFromPath } from '~/utils/PathUtils';
import { useAirStore } from '~/utils/ReduxUtils';

export type DeleteCommentClickParams = {
  comment: CommentResponse;
  clipId: Clip['id'];
  timestamp?: number;
  annotation?: Annotation;
};

export type DeleteCommentClick = (params: DeleteCommentClickParams) => void;

/**
 * `shortId` picks the API family the comment is deleted through: a share link's shortId routes to
 * the public share-link endpoint, `undefined` routes to the private app's authenticated endpoint.
 * Components never call this hook directly — the area-owned twins
 * (`usePublicDeleteCommentClick` / `usePrivateDeleteCommentClick`) bind the source.
 */
export const useDeleteCommentClick = ({
  trackLocation,
  shortId,
}: {
  trackLocation: DiscussionLocation;
  shortId: string | undefined;
}) => {
  const store = useAirStore();
  const { deleteComment } = useDeleteComment();
  const assetId = useSelector(centralizedClipAssetIdSelector);
  const { trackDeletedComment } = useTrackDeletedComment();

  const { updateStateAfterAddingOrRemovingDiscussion } = useUpdateStateAfterAddingOrRemovingDiscussion();

  const handleCommentDeleteClick = useCallback(
    ({ comment, clipId, timestamp, annotation }: DeleteCommentClickParams) => {
      const boardId = getBoardIdFromPath(window.location.pathname);
      const { openCommentCount, openDiscussionCount } = centralizedClipSelector(store.getState());

      trackDeletedComment({
        boardId,
        clipId,
        location: trackLocation,
        public: trackLocation === 'public-board',
        has_timestamp: !isUndefined(timestamp),
        has_annotation: isAnnotationCorrect(annotation),
        annotationType: getAnnotationAnalyticsType(annotation),
        assetType: centralizedClipTypeSelector(store.getState()),
      });

      const decrementedCommentCount = openCommentCount ? openCommentCount - 1 : 0;
      const decrementedDiscussionCount = openDiscussionCount ? openDiscussionCount - 1 : 0;

      deleteComment({
        existingComment: comment,
        boardId,
        shortId,
        assetId,
      });
      updateStateAfterAddingOrRemovingDiscussion({
        newCommentCount: decrementedCommentCount,
        newDiscussionCount: decrementedDiscussionCount,
        clipId,
      });
    },
    [
      store,
      trackDeletedComment,
      trackLocation,
      deleteComment,
      assetId,
      shortId,
      updateStateAfterAddingOrRemovingDiscussion,
    ],
  );

  return {
    handleCommentDeleteClick,
  };
};
